import { useEffect, useState } from "react";
import { Landmark, Loader2, Upload, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useFideicomiso } from "../lib/fideicomiso";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Tesoreria() {
    const { selected } = useFideicomiso();
    const [cuentas, setCuentas] = useState([]);
    const [expanded, setExpanded] = useState(null);
    const [movs, setMovs] = useState({});
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        if (!selected) return;
        const { data } = await api.get(`/fideicomisos/${selected.id}/cuentas`);
        setCuentas(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected?.id]);

    const loadMovs = async (cuentaId) => {
        const { data } = await api.get(`/cuentas/${cuentaId}/movimientos`);
        setMovs((m) => ({ ...m, [cuentaId]: data }));
    };
    const toggle = (id) => {
        setExpanded(expanded === id ? null : id);
        if (expanded !== id && !movs[id]) loadMovs(id);
    };

    const doImport = async (cuentaId) => {
        if (!file) return toast.error("Selecciona archivo");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("archivo", file);
            const { data } = await api.post(`/cuentas/${cuentaId}/import`, fd);
            toast.success(`Import: ${data.creados} creados · ${data.conciliados} conciliados`);
            loadMovs(cuentaId);
            setFile(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    if (!selected) {
        return <div className="p-10 text-muted-foreground text-sm">Selecciona un fideicomiso en el header.</div>;
    }

    return (
        <div className="p-8 lg:p-10 max-w-[1400px] space-y-6" data-testid="tesoreria-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                    Núcleo · {selected.numero_interno}
                </div>
                <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                    <Landmark className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                    Cuentas y Tesorería
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    <span className="badge-mock">MOCK</span> Import de estados de cuenta bancario;
                    Fase 3 conectará H2H.
                </p>
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <table className="trf-table" data-testid="cuentas-table">
                    <thead><tr>
                        <th>Banco</th><th>Cuenta</th><th>Moneda</th><th>Titular</th>
                        <th>Saldo libro</th><th>Estado</th><th></th>
                    </tr></thead>
                    <tbody>
                        {cuentas.map((c) => (
                            <>
                                <tr key={c.id} data-testid={`cuenta-row-${c.id}`}>
                                    <td className="font-medium">{c.banco}</td>
                                    <td className="tabular text-xs">{c.numero_cuenta}</td>
                                    <td><span className="badge-gold">{c.moneda}</span></td>
                                    <td className="text-xs">{c.titular_legal}</td>
                                    <td className="tabular font-medium">
                                        {c.saldo_libro?.toLocaleString("es-MX", { style: "currency", currency: c.moneda })}
                                    </td>
                                    <td>
                                        <span className={c.estado === "congelada" ? "badge-mock" : c.estado === "activa" ? "badge-exito" : "badge-gold"}>
                                            {c.estado}
                                        </span>
                                    </td>
                                    <td>
                                        <Button size="sm" variant="ghost" onClick={() => toggle(c.id)} data-testid={`toggle-cuenta-${c.id}`}>
                                            {expanded === c.id ? "Cerrar" : "Movimientos"}
                                        </Button>
                                    </td>
                                </tr>
                                {expanded === c.id && (
                                    <tr>
                                        <td colSpan={7} className="bg-muted/40 p-4">
                                            <div className="flex items-end gap-2 mb-3">
                                                <Input type="file" accept=".csv,.xlsx"
                                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                                    data-testid={`import-file-${c.id}`} />
                                                <Button size="sm" onClick={() => doImport(c.id)} disabled={busy}
                                                    data-testid="import-estado-cuenta-btn">
                                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : (<><Upload className="h-3 w-3 mr-1" /> Importar estado de cuenta</>)}
                                                </Button>
                                            </div>
                                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                                                Movimientos ({(movs[c.id] || []).length})
                                            </div>
                                            <div className="max-h-56 overflow-y-auto rounded-sm border border-border bg-card">
                                                <table className="trf-table text-xs">
                                                    <thead><tr><th>Fecha</th><th>Tipo</th><th>Monto</th><th>Ref</th><th>Concepto</th><th>Concil.</th></tr></thead>
                                                    <tbody>
                                                        {(movs[c.id] || []).map((m) => (
                                                            <tr key={m.id}>
                                                                <td className="tabular">{m.fecha}</td>
                                                                <td>{m.tipo}</td>
                                                                <td className="tabular">{m.monto?.toLocaleString()}</td>
                                                                <td className="hash-cell">{m.referencia}</td>
                                                                <td>{m.concepto}</td>
                                                                <td>
                                                                    <span className={m.estado_conciliacion === "conciliado" ? "badge-exito" : "badge-gold"}>
                                                                        {m.estado_conciliacion}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {(movs[c.id] || []).length === 0 && (
                                                            <tr><td colSpan={6} className="text-center py-4 text-muted-foreground">Sin movimientos.</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
